// localStorage-backed persistence: VS-AI stats, unlocked difficulty tiers, and
// the player's display name. All reads are defensive (private mode / quota).

export interface AiStats {
  battles: number;
  wins: number;
  losses: number;
  roundsWon: number;
  roundsLost: number;
  bestStreak: number;
  streak: number;
  koDealt: number;
  crowStrikes: number;
}

export interface SaveData {
  name: string;
  stats: AiStats;
  hardUnlocked: boolean;
  ultimateUnlocked: boolean;
  /** Audio mute preference — persists across visits (core/audio.ts). */
  muted: boolean;
  /** Plumage (weapon) tier 1–3: upgraded by BANTAM/ROOSTER wins (unlocks.json). */
  plumage: number;
  /**
   * Super-move cinematics already watched to completion, as "<classId>:S"
   * (skill) / "<classId>:L" (limit) keys — a cinematic becomes tap-skippable
   * only after its first full view.
   */
  cineSeen: string[];
}

const KEY = "rsb.save.v1";

const DEFAULT: SaveData = {
  name: "",
  stats: {
    battles: 0,
    wins: 0,
    losses: 0,
    roundsWon: 0,
    roundsLost: 0,
    bestStreak: 0,
    streak: 0,
    koDealt: 0,
    crowStrikes: 0,
  },
  hardUnlocked: false,
  ultimateUnlocked: false,
  muted: false,
  plumage: 1,
  cineSeen: [],
};

export class Storage {
  data: SaveData;

  constructor() {
    this.data = this.read();
  }

  private read(): SaveData {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return structuredClone(DEFAULT);
      const parsed = JSON.parse(raw) as Partial<SaveData>;
      return {
        ...structuredClone(DEFAULT),
        ...parsed,
        stats: { ...DEFAULT.stats, ...(parsed.stats ?? {}) },
        muted: parsed.muted === true,
        cineSeen: Array.isArray(parsed.cineSeen)
          ? parsed.cineSeen.filter((k): k is string => typeof k === "string")
          : [],
      };
    } catch {
      return structuredClone(DEFAULT);
    }
  }

  save(): void {
    try {
      localStorage.setItem(KEY, JSON.stringify(this.data));
    } catch {
      /* ignore quota / private mode */
    }
  }
}
